import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, MessageSquare } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import ActionDialog from "./action-dialog";
import { UserRole } from "./actions";
import { ASSIGN_ENTRY } from "./actions/assignment-entries";
import { appointment_status } from "@/models/appointment.model";
import { uuid } from "@/interfaces";
import { useAppointmentActions } from "./appointment-actions.hooks";
import { AssignmentModal } from "@/components/shared/assignment-modal";
import { assignAppointment } from "@/services/appointment-assignment.service";
import { assignmentChangesNothing, type AssigningHat, type ResourceAssignment } from "@/lib/appointment-assignment";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { AppointmentsRepository } from "@/repositories/appointments.repository";
import { toast } from "sonner";
import AppointmentCommunicationSheet from "./AppointmentCommunicationSheet";

interface AppointmentActionBarProps {
  appointmentId: uuid;
  currentStatus: appointment_status;
  currentDate?: string;
  userRole: UserRole;
  maxVisibleActions?: number;
  className?: string;
  vendorId?: uuid;
  serviceId?: uuid;
  roomId?: uuid;
}

export default function AppointmentActionBar({
  appointmentId,
  currentStatus,
  currentDate,
  userRole,
  maxVisibleActions = 3,
  className,
  vendorId,
  serviceId,
  roomId,
}: AppointmentActionBarProps) {
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [appointment, setAppointment] = useState<any>(null);
  const assigningHat: AssigningHat = userRole === UserRole.SUBS ? "subcontractor" : "vendor";
  const [communicationSheetOpen, setCommunicationSheetOpen] = useState(false);

  const hookResult = useAppointmentActions({
    appointmentId,
    currentStatus,
    currentDate,
    userRole,
    vendorId,
    serviceId,
  });

  const {
    availableActions,
    dialogOpen,
    selectedAction,
    handleActionClick: originalHandleActionClick,
    handleDialogConfirm,
    closeDialog,
    context,
    ...hookState
  } = hookResult;

  // Load appointment data when needed
  const loadAppointment = async () => {
    try {
      const appt = await AppointmentsRepository.getById(appointmentId);
      setAppointment(appt);
    } catch (error) {
      console.error("Failed to load appointment:", error);
      toast.error("Failed to load appointment details");
    }
  };

  const handleOpenAssignmentModal = async () => {
    await loadAppointment();
    setAssignmentModalOpen(true);
  };

  const handleAssignResource = async (assignment: ResourceAssignment) => {
    setIsAssigning(true);
    try {
      const changesNothing = appointment !== null && assignmentChangesNothing(assigningHat, assignment, appointment);
      await assignAppointment(appointmentId, assignment);
      if (changesNothing) toast.info("No changes");
      else toast.success(assignment.type === "individual" ? "Individual assigned successfully" : "Subcontractor assigned successfully");
      setAssignmentModalOpen(false);
    } catch (error) {
      console.error("Assignment failed:", error);
      toast.error(fieldActionMessage(error, "Failed to assign resource"));
      throw error;
    } finally {
      setIsAssigning(false);
    }
  };

  const handleActionClick = (actionId: string) => {
    if (actionId === ASSIGN_ENTRY.config.id) {
      handleOpenAssignmentModal();
    } else if (actionId === "add_comment") {
      setCommunicationSheetOpen(true);
    } else {
      originalHandleActionClick(actionId);
    }
  };

  if (availableActions.length === 0) {
    return null;
  }

  const visibleActions = availableActions.slice(0, maxVisibleActions);
  const overflowActions = availableActions.slice(maxVisibleActions);

  return (
    <>
      <div className={cn("flex items-center gap-2 p-3 border-t bg-muted/30", className)}>
        <span className="text-xs text-muted-foreground font-medium mr-2">Quick Actions:</span>

        {/* Visible action buttons */}
        {visibleActions.map((action) => {
          const Icon = action.config.icon;
          return (
            <Button
              key={action.config.id}
              size="sm"
              variant="outline"
              onClick={() => handleActionClick(action.config.id)}
              className="h-8 px-3 text-xs"
            >
              <Icon className="w-3 h-3 mr-1.5" />
              {action.config.label}
            </Button>
          );
        })}

        {/* Communication Log button */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setCommunicationSheetOpen(true)}
          className="h-8 px-3 text-xs"
        >
          <MessageSquare className="w-3 h-3 mr-1.5" />
          Comms Log
        </Button>

        {/* Overflow menu */}
        {overflowActions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 px-2" aria-label="More actions">
                <MoreHorizontal className="w-3 h-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {overflowActions.map((action) => {
                const Icon = action.config.icon;
                return (
                  <DropdownMenuItem
                    key={action.config.id}
                    onClick={() => handleActionClick(action.config.id)}
                    className={cn(action.config.variant === "destructive" && "text-destructive focus:text-destructive")}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    <div>
                      <div className="font-medium">{action.config.label}</div>
                      {action.config.description && (
                        <div className="text-xs text-muted-foreground">{action.config.description}</div>
                      )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Action Dialog */}
      {selectedAction && (
        <ActionDialog
          action={selectedAction}
          context={context}
          open={dialogOpen}
          onConfirm={handleDialogConfirm}
          onCancel={closeDialog}
          hookState={hookState}
        />
      )}

      {/* Assignment Modal */}
      {appointment && (
        <AssignmentModal
          isOpen={assignmentModalOpen}
          onClose={() => setAssignmentModalOpen(false)}
          appointment={appointment}
          onAssign={handleAssignResource}
          isLoading={isAssigning}
          hat={assigningHat}
        />
      )}

      {/* Communication Log Sheet */}
      <AppointmentCommunicationSheet
        appointmentId={appointmentId}
        userRole={userRole}
        open={communicationSheetOpen}
        onOpenChange={setCommunicationSheetOpen}
      />
    </>
  );
}
